import { AddIcon, EditIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  getAdminBookClubs,
  getAdminCommunityReports,
  updateAdminCommunityReport,
} from "../../../services/communityApi";

const userHandleLabel = (user) => {
  if (user?.userHandle) {
    return `@${user.userHandle}`;
  }

  return user?.id ? `User #${user.id}` : "Unknown user";
};

const reportDateLabel = (createdAt) => {
  if (!createdAt) {
    return "Unknown date";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(createdAt));
};

const reasonLabel = (reason) =>
  ({
    scam: "Scam or spam",
    harassment: "Harassment or bullying",
    spoilers: "Spoilers",
    inappropriate: "Inappropriate content",
  }[reason] || reason);

const statusColor = {
  open: "yellow",
  dismissed: "gray",
  hidden: "red",
  actioned: "green",
};

const CommunityAdmin = ({ embedded = false }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const clubs = useQuery("adminCommunityBookClubs", getAdminBookClubs);
  const reports = useQuery("adminCommunityReports", getAdminCommunityReports);
  const updateReport = useMutation(
    ({ id, status }) => updateAdminCommunityReport(id, status),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries("adminCommunityReports");
        queryClient.invalidateQueries("adminCommunityBookClubs");

        if (variables.status === "hidden") {
          queryClient.invalidateQueries("communityBookClub");
        }

        toast({
          title:
            variables.status === "hidden"
              ? "Reported content hidden."
              : "Report dismissed.",
          description:
            variables.status === "hidden"
              ? "It is no longer visible in the Book Club."
              : "The content remains visible.",
          status: "success",
        });
      },
      onError: () => {
        toast({ title: "Report could not be updated.", status: "error" });
      },
    }
  );

  const header = (
    <Flex align="center" justify="space-between" gap="4">
      <Box>
        <Heading>The Panel admin</Heading>
        <Text mt="2" color="gray.400">
          Create and maintain the book clubs shown to the community.
        </Text>
      </Box>
      <Button
        colorScheme="red"
        leftIcon={<AddIcon />}
        onClick={() => navigate("/bookclub/admin/book-clubs/new")}
      >
        New book club
      </Button>
    </Flex>
  );

  const content = (
    <>
      <Box bg="gray.700" borderRadius="lg" p="6" mt={embedded ? 0 : 8}>
        <Heading size="md">Book clubs</Heading>
        <Stack mt="5" spacing="4">
          {clubs.data?.data?.length ? (
            clubs.data.data.map((club) => (
              <Flex
                key={club.id}
                align={{ base: "start", md: "center" }}
                direction={{ base: "column", md: "row" }}
                gap="4"
                borderWidth="1px"
                borderColor="gray.600"
                borderRadius="md"
                p="4"
              >
                {club.cover ? (
                  <Box
                    w={{ base: "full", md: "120px" }}
                    h="76px"
                    borderRadius="md"
                    bgImage={`url(${club.cover})`}
                    bgSize="cover"
                    bgPosition="center"
                    flexShrink={0}
                  />
                ) : null}
                <Box flex="1">
                  <Flex align="center" gap="3" wrap="wrap">
                    <Heading size="sm">{club.title}</Heading>
                    <Text
                      color={club.published ? "green.300" : "yellow.300"}
                      fontSize="sm"
                      textTransform="capitalize"
                    >
                      {club.publicationStatus ||
                        (club.published ? "published" : "draft")}
                    </Text>
                  </Flex>
                  <Text mt="2" color="gray.400" fontSize="sm">
                    {club.startsAt} – {club.endsAt} · {club.status} ·{" "}
                    {club.memberCount} members ·{" "}
                    {club.readingOrder ? "1 reading order" : "No reading order"}{" "}
                    ·{" "}
                    {club.threads?.filter((thread) => !thread.hiddenAt)
                      .length || 0}{" "}
                    leading questions
                  </Text>
                </Box>
                <Button
                  variant="outline"
                  leftIcon={<EditIcon />}
                  onClick={() =>
                    navigate(`/bookclub/admin/book-clubs/${club.id}/edit`)
                  }
                >
                  Edit
                </Button>
              </Flex>
            ))
          ) : (
            <Text color="gray.400">No book clubs have been created yet.</Text>
          )}
        </Stack>
      </Box>

      <Box bg="gray.700" borderRadius="lg" p="6" mt="6">
        <Heading size="md">Report queue</Heading>
        <Text mt="2" color="gray.400" fontSize="sm">
          Dismiss closes a report without changing the content. Hide removes the
          reported comment or discussion from the Book Club.
        </Text>
        <TableContainer mt="4">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Reported</Th>
                <Th>Reported by</Th>
                <Th>Content</Th>
                <Th>Reason</Th>
                <Th>Status</Th>
                <Th>Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {reports.data?.data?.length ? (
                reports.data.data.map((report) => {
                  const isUpdating =
                    updateReport.isLoading &&
                    updateReport.variables?.id === report.id;

                  return (
                    <Tr key={report.id}>
                      <Td whiteSpace="nowrap">
                        <Text fontSize="sm">
                          {reportDateLabel(report.createdAt)}
                        </Text>
                      </Td>
                      <Td whiteSpace="nowrap">
                        {userHandleLabel(report.reporter)}
                      </Td>
                      <Td minW="280px" maxW="440px">
                        <Text fontSize="xs" color="gray.400">
                          {report.targetType === "thread"
                            ? "Discussion"
                            : "Comment"}{" "}
                          by {userHandleLabel(report.author)}
                        </Text>
                        {report.target?.title ? (
                          <Text mt="1" fontWeight="semibold">
                            {report.target.title}
                          </Text>
                        ) : null}
                        <Text mt="1" noOfLines={3} whiteSpace="pre-wrap">
                          {report.target?.body || "Content is unavailable."}
                        </Text>
                      </Td>
                      <Td>{reasonLabel(report.reason)}</Td>
                      <Td>
                        <Badge
                          colorScheme={statusColor[report.status] || "gray"}
                        >
                          {report.status}
                        </Badge>
                      </Td>
                      <Td whiteSpace="nowrap">
                        {report.status === "open" ? (
                          <>
                            <Button
                              size="xs"
                              isDisabled={updateReport.isLoading}
                              isLoading={
                                isUpdating &&
                                updateReport.variables?.status === "dismissed"
                              }
                              onClick={() =>
                                updateReport.mutate({
                                  id: report.id,
                                  status: "dismissed",
                                })
                              }
                            >
                              Dismiss
                            </Button>
                            <Button
                              size="xs"
                              ml="2"
                              colorScheme="red"
                              isDisabled={updateReport.isLoading}
                              isLoading={
                                isUpdating &&
                                updateReport.variables?.status === "hidden"
                              }
                              onClick={() =>
                                updateReport.mutate({
                                  id: report.id,
                                  status: "hidden",
                                })
                              }
                            >
                              Hide
                            </Button>
                          </>
                        ) : null}
                      </Td>
                    </Tr>
                  );
                })
              ) : (
                <Tr>
                  <Td colSpan={6} color="gray.400">
                    {reports.isLoading
                      ? "Loading reports…"
                      : "No reports have been submitted."}
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Container maxW="container.xl" py="8">
      {header}
      {content}
    </Container>
  );
};

export default CommunityAdmin;
